import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, authAxios } from '../../contexts/AuthContext';
import { Search, Download, ChevronLeft, ChevronRight, IndianRupee, Trash2 } from 'lucide-react';
import { AdminHeader } from '../../components/AdminHeader';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminClients() {
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [financeDialog, setFinanceDialog] = useState(null);
  const [financeForm, setFinanceForm] = useState({ total_amount: '', advance: '', due_date: '', description: '' });
  const [clientOrders, setClientOrders] = useState([]);

  useEffect(() => { fetchClients(); }, [page, search]);

  const fetchClients = async () => {
    setLoading(true);
    try {
      const { data } = await authAxios.get(`${API}/clients?page=${page}&limit=20&q=${encodeURIComponent(search)}`);
      setClients(data.clients);
      setTotal(data.total);
      setPages(data.pages);
    } catch (err) {
      console.error('Failed:', err);
    }
    setLoading(false);
  };

  const openFinanceDialog = async (client) => {
    try {
      const { data } = await authAxios.get(`${API}/clients/${client.id}`);
      setClientOrders(data.orders || []);
      setFinanceDialog(client);
    } catch {}
  };

  const handleFinanceUpdate = async (orderId) => {
    try {
      await authAxios.put(`${API}/admin/orders/${orderId}/finance`, {
        total_amount: parseFloat(financeForm.total_amount) || 0,
        advance: parseFloat(financeForm.advance) || 0,
        due_date: financeForm.due_date,
        description: financeForm.description,
      });
      toast.success('Finance updated');
      setFinanceDialog(null);
      setFinanceForm({ total_amount: '', advance: '', due_date: '', description: '' });
    } catch (err) {
      toast.error('Failed to update finance');
    }
  };

  const handleDeleteClient = async (c) => {
    if (!window.confirm(`Permanently delete ${c.name} and all their orders? This cannot be undone.`)) return;
    try {
      const { data } = await authAxios.delete(`${API}/clients/${c.id}`);
      toast.success(data.message || 'Client deleted');
      fetchClients();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete client');
    }
  };

  return (
    <div className="min-h-screen bg-[#F5F5F5]">
      <AdminHeader />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-5 sm:py-8">
        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-sans font-bold text-[#1C1C1E]" style={{ fontFamily: "'DM Sans', sans-serif" }}>
            All Clients ({total})
          </h1>
          <div className="flex gap-3">
            <Button
              data-testid="export-clients-csv"
              variant="outline"
              className="border-[#E5E5EA] rounded-2xl text-sm"
              onClick={async () => {
                const response = await authAxios.get(`${API}/admin/export/clients`, { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const a = document.createElement('a'); a.href = url; a.download = 'clients.csv'; a.click();
              }}
            >
              <Download className="w-4 h-4 mr-2" strokeWidth={1.5} />
              Export CSV
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-[#8E8E93]" strokeWidth={1.5} />
          <Input
            data-testid="admin-client-search"
            className="h-12 pl-12 border-[#E5E5EA] bg-white rounded-2xl"
            placeholder="Search clients..."
            value={search}
            onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          />
        </div>

        {/* Client Table */}
        {loading ? <p className="text-[#8E8E93]">Loading...</p> : (
          <>
            <div className="bg-white border border-[#E5E5EA] rounded-2xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-[#F5F5F5]">
                      <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wide text-[#8E8E93] border-b border-[#E5E5EA]">#</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wide text-[#8E8E93] border-b border-[#E5E5EA]">Name</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wide text-[#8E8E93] border-b border-[#E5E5EA]">Phone</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wide text-[#8E8E93] border-b border-[#E5E5EA]">Gender</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wide text-[#8E8E93] border-b border-[#E5E5EA]">Date</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold uppercase tracking-wide text-[#8E8E93] border-b border-[#E5E5EA]">Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clients.map((c) => (
                      <tr key={c.id} className="hover:bg-[#F5F5F5]/50 transition-colors">
                        <td className="py-3 px-4 text-sm text-[#1C1C1E] border-b border-[#E5E5EA]">{c.client_number}</td>
                        <td className="py-3 px-4 text-sm font-medium text-[#1C1C1E] border-b border-[#E5E5EA]">{c.name}</td>
                        <td className="py-3 px-4 text-sm text-[#8E8E93] border-b border-[#E5E5EA]">{c.phone}</td>
                        <td className="py-3 px-4 border-b border-[#E5E5EA]">
                          <Badge className={`${c.gender === 'male' ? 'bg-blue-50 text-blue-700' : 'bg-pink-50 text-pink-700'} border-0 text-xs`}>{c.gender}</Badge>
                        </td>
                        <td className="py-3 px-4 text-sm text-[#8E8E93] border-b border-[#E5E5EA]">
                          {c.created_at ? new Date(c.created_at).toLocaleDateString('en-IN') : '-'}
                        </td>
                        <td className="py-3 px-4 border-b border-[#E5E5EA]">
                          <div className="flex gap-2">
                            <Button
                              variant="outline" size="sm"
                              className="border-[#E5E5EA] rounded-2xl text-xs"
                              data-testid={`view-client-${c.client_number}`}
                              onClick={() => navigate(`/client/${c.id}`)}
                            >
                              View
                            </Button>
                            <Button
                              variant="outline" size="sm"
                              className="border-[#E5E5EA] rounded-2xl text-xs text-[#1C1C1E]"
                              data-testid={`finance-client-${c.client_number}`}
                              onClick={() => openFinanceDialog(c)}
                            >
                              <IndianRupee className="w-3 h-3 mr-1" />
                              Finance
                            </Button>
                            <Button
                              variant="outline" size="sm"
                              className="border-red-200 text-red-600 hover:bg-red-50 rounded-2xl text-xs"
                              data-testid={`delete-client-${c.client_number}`}
                              onClick={() => handleDeleteClient(c)}
                            >
                              <Trash2 className="w-3 h-3" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
            {/* Pagination */}
            {pages > 1 && (
              <div className="flex items-center justify-between mt-4">
                <p className="text-sm text-[#8E8E93]">Page {page} of {pages}</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" className="border-[#E5E5EA] rounded-2xl" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" className="border-[#E5E5EA] rounded-2xl" disabled={page >= pages} onClick={() => setPage(p => p + 1)}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Finance Dialog */}
      <Dialog open={!!financeDialog} onOpenChange={() => setFinanceDialog(null)}>
        <DialogContent className="bg-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-sans font-bold text-xl" style={{ fontFamily: "'DM Sans', sans-serif" }}>
              Finance - {financeDialog?.name}
            </DialogTitle>
            <DialogDescription>Update payment details for orders</DialogDescription>
          </DialogHeader>
          {clientOrders.length === 0 ? (
            <p className="text-sm text-[#8E8E93] py-4">No orders found for this client.</p>
          ) : (
            <div className="space-y-4 mt-2 max-h-[60vh] overflow-y-auto">
              {clientOrders.map(order => (
                <Card key={order.id} className="border-[#E5E5EA]">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between mb-3">
                      <span className="font-medium text-[#1C1C1E]">Order #{order.order_number} - {order.garment_type?.replace('_', ' ')}</span>
                    </div>
                    {order.finance && Object.keys(order.finance).length > 0 && (
                      <div className="grid grid-cols-3 gap-2 mb-3 text-sm">
                        <div className="bg-[#F5F5F5] p-2 rounded-2xl">
                          <span className="text-xs text-[#8E8E93]">Total</span>
                          <p className="font-medium">{order.finance.total_amount || 0}</p>
                        </div>
                        <div className="bg-[#F5F5F5] p-2 rounded-2xl">
                          <span className="text-xs text-[#8E8E93]">Advance</span>
                          <p className="font-medium text-green-700">{order.finance.advance || 0}</p>
                        </div>
                        <div className="bg-[#F5F5F5] p-2 rounded-2xl">
                          <span className="text-xs text-[#8E8E93]">Balance</span>
                          <p className="font-medium text-red-600">{order.finance.balance || 0}</p>
                        </div>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-[#8E8E93]">Total Amount</Label>
                        <Input className="h-9 text-sm border-[#E5E5EA] rounded-2xl" type="number" placeholder="0"
                          onChange={(e) => setFinanceForm(f => ({ ...f, total_amount: e.target.value }))} />
                      </div>
                      <div>
                        <Label className="text-xs text-[#8E8E93]">Advance</Label>
                        <Input className="h-9 text-sm border-[#E5E5EA] rounded-2xl" type="number" placeholder="0"
                          onChange={(e) => setFinanceForm(f => ({ ...f, advance: e.target.value }))} />
                      </div>
                      <div>
                        <Label className="text-xs text-[#8E8E93]">Due Date</Label>
                        <Input className="h-9 text-sm border-[#E5E5EA] rounded-2xl" type="date"
                          onChange={(e) => setFinanceForm(f => ({ ...f, due_date: e.target.value }))} />
                      </div>
                      <div>
                        <Label className="text-xs text-[#8E8E93]">Description</Label>
                        <Input className="h-9 text-sm border-[#E5E5EA] rounded-2xl" placeholder="e.g. Silk Lehenga"
                          onChange={(e) => setFinanceForm(f => ({ ...f, description: e.target.value }))} />
                      </div>
                    </div>
                    <Button
                      data-testid={`save-finance-${order.order_number}`}
                      className="mt-3 h-9 w-full bg-[#1C1C1E] text-white hover:bg-[#2C2C2E] rounded-2xl text-sm"
                      onClick={() => handleFinanceUpdate(order.id)}
                    >
                      Update Finance
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
